import { collapseWhitespace } from "./text.js";
/**
 * Normalisation and near-match detection. The picker, the write path and the
 * importer all call these, which is the only thing that stops the three from
 * drifting apart — an importer that normalises differently from the picker
 * seeds the suggestion lists with the inconsistencies the picker exists to
 * prevent.
 *
 * The module keeps two ideas apart, and the difference between them is the
 * whole design:
 *
 * **`normaliseName` decides what merges automatically.** It is the sortKey the
 * unique index is built on, so two values sharing it cannot both exist. It
 * folds case and whitespace and nothing else, because those are the variants
 * where no judgement is involved and nothing can be lost: `bollinger` and
 * `Bollinger` are the same producer, and `\tMarc Hébrart` is the same producer
 * as `Marc Hébrart`.
 *
 * **`findNearMatches` decides what is offered.** Everything that is not
 * plainly the same string — a dropped accent, a missing letter, a name written
 * with its estate word and without — is detected and shown as *did you mean*,
 * and applied only by the person who knows the answer. Nothing here ever
 * merges anything.
 *
 * That split is deliberate. `Gosset` and `Gosset-Brabant` are different
 * Champagne houses, `Domaine Leflaive` and `Olivier Leflaive` are different
 * producers in the same village, and `Pinot Blanc` and `Pinot Noir` differ by
 * four characters and are not the same grape. An algorithm that merges by
 * similarity destroys distinctions like those silently, in a dataset built
 * over twenty years, and the damage is discovered long after the evidence is
 * gone.
 */
/**
 * The sort and match key: what the unique index is built on, and therefore
 * exactly what merges without anyone being asked. Case and whitespace only.
 */
export function normaliseName(value) {
    return collapseWhitespace(value).toLowerCase();
}
/**
 * Words that describe the kind of estate rather than name it. Stripping these
 * is what lets `Luigi Einaudi` find `Poderi Luigi Einaudi` while leaving
 * `Gosset` and `Gosset-Brabant` apart: the difference in the first pair is
 * entirely a generic word, and in the second it is a family name.
 *
 * Kept deliberately short. Every addition widens what is offered, and a word
 * added here that is really part of a name — `Clos`, in `Clos de Vougeot` —
 * would start proposing merges between genuinely different places. Suggestions
 * are dismissible, so the cost of a wrong entry is small, but it is not zero.
 */
const ESTATE_WORDS = new Set([
    "domaine",
    "domaines",
    "chateau",
    "castello",
    "casa",
    "poderi",
    "podere",
    "tenuta",
    "azienda",
    "agricola",
    "fattoria",
    "cantina",
    "cantine",
    "weingut",
    "bodega",
    "bodegas",
    "maison",
    "champagne",
    "estate",
    "winery",
    "vignobles",
    "marchesi",
    "et",
    "fils",
    "figli",
    "sons",
]);
/**
 * Strip the accents from a value without touching the letters underneath.
 *
 * Exported because the wine filter folds the same way when it decides whether
 * `hebrart` finds `Hébrart`. Folding is an offering rule, never a storage one:
 * nothing written to the database passes through here, and no two names are
 * merged because they agree once their accents are gone.
 */
export function foldAccents(value) {
    return value.normalize("NFD").replace(/\p{Diacritic}/gu, "");
}
/**
 * The looser key used for offering, never for storing. Folds accents, turns
 * punctuation into word boundaries, and drops the estate words — so
 * `Poderi Luigi Einaudi` and `Luigi Einaudi` arrive at the same place, while
 * `Gosset-Brabant` and `Gosset` do not.
 */
function matchKey(value) {
    const words = foldAccents(normaliseName(value))
        .replace(/[^\p{Letter}\p{Number}]+/gu, " ")
        .split(" ")
        .filter((word) => word !== "" && !ESTATE_WORDS.has(word));
    // Everything was an estate word: compare on the original rather than on
    // nothing, or every such name would match every other.
    return words.length === 0 ? foldAccents(normaliseName(value)) : words.join(" ");
}
/** Levenshtein distance, abandoned once it is certain to exceed `limit`. */
function distanceWithin(a, b, limit) {
    if (Math.abs(a.length - b.length) > limit)
        return limit + 1;
    let previous = Array.from({ length: b.length + 1 }, (_, i) => i);
    for (let i = 1; i <= a.length; i += 1) {
        const current = [i];
        let best = i;
        for (let j = 1; j <= b.length; j += 1) {
            const substitution = (previous[j - 1] ?? 0) + (a[i - 1] === b[j - 1] ? 0 : 1);
            const insertion = (current[j - 1] ?? 0) + 1;
            const deletion = (previous[j] ?? 0) + 1;
            const value = Math.min(substitution, insertion, deletion);
            current.push(value);
            best = Math.min(best, value);
        }
        if (best > limit)
            return limit + 1;
        previous = current;
    }
    return previous[b.length] ?? limit + 1;
}
/**
 * How wrong a spelling may be before it stops being a plausible typo. Scaled
 * to length, because one wrong letter in four is a different word while one in
 * twelve is a slip of the finger.
 *
 * The ceiling of two is a judgement rather than a boundary. It is not what
 * separates the pairs that must stay apart — `Pinot Blanc` and `Pinot Noir`
 * are five apart, and every other pair in the scope is further still — so
 * loosening it would not merge anything that matters. What it decides is how
 * much noise the picker makes, since a suggestion is offered and never
 * applied. Tightening it costs a missed prompt; loosening it costs an
 * irritating one. The test that pins it is deliberately synthetic, because no
 * real name in this cellar sits near the line.
 */
function typoAllowance(length) {
    if (length <= 4)
        return 0;
    if (length <= 8)
        return 1;
    return 2;
}
/**
 * Existing names that the query may have been meant to be, best first.
 *
 * A candidate identical under `normaliseName` is not returned: that is not a
 * near match but the same row, and the caller uses it directly.
 */
export function findNearMatches(query, existing) {
    const queryKey = normaliseName(query);
    if (queryKey === "")
        return [];
    const queryMatch = matchKey(query);
    const matches = [];
    const seen = new Set();
    for (const candidate of existing) {
        const candidateKey = normaliseName(candidate);
        if (candidateKey === queryKey || seen.has(candidateKey))
            continue;
        seen.add(candidateKey);
        if (matchKey(candidate) === queryMatch) {
            matches.push({ value: candidate, reason: "same-name-different-spelling" });
            continue;
        }
        const limit = typoAllowance(Math.max(queryKey.length, candidateKey.length));
        if (limit > 0 && distanceWithin(queryKey, candidateKey, limit) <= limit) {
            matches.push({ value: candidate, reason: "possible-typo" });
        }
    }
    // The stronger reason first: a name written with and without its estate word
    // is a surer suggestion than a guess at a mistyped letter.
    return matches.sort((a, b) => a.reason === b.reason ? 0 : a.reason === "same-name-different-spelling" ? -1 : 1);
}
//# sourceMappingURL=matching.js.map